import CalendarFolderTypeEnum from 'owa-service/lib/contract/CalendarFolderTypeEnum';
import GetCalendarFolderConfigurationResponse from 'owa-service/lib/contract/GetCalendarFolderConfigurationResponse';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import LocalCacheForRemoteCalendarEntry from 'owa-service/lib/contract/LocalCacheForRemoteCalendarEntry';
import { createExplicitLogonRequest } from 'owa-headers';
import { getCalendarFolderConfigurationService } from '../services/getCalendarFolderConfigurationService';
import folderId from 'owa-service/lib/factory/folderId';
import {
    addToCalendarsCache,
    markCalendarEntryAsValid,
    calendarUpdated,
    getFolderIdByCalendarID,
    getCalculatedFolderIdByCalendarID,
} from 'owa-calendar-cache';
import { MailboxInfo } from 'owa-client-ids';
import {
    getCalendarIdByFolderId,
    getCalendarEntryByCalendarId,
} from '../selectors/calendarsCacheSelectors';
import { removeCalendarWithIDFromCalendarsCache } from '../actions/internalActions';
import { getUserMailboxInfo } from 'owa-client-ids';
import { logUsage } from 'owa-analytics';

export async function getAndUpdateActualFolderId(calendarId: string) {
    const calendarEntry: LocalCacheForRemoteCalendarEntry = getCalendarEntryByCalendarId(
        calendarId
    ) as LocalCacheForRemoteCalendarEntry;
    const calendarFolderId = getFolderIdByCalendarID(calendarId);
    const calculatedFolderId = getCalculatedFolderIdByCalendarID(calendarId);
    const fullCalendarId = getCalendarIdByFolderId(calendarFolderId);
    const mailboxInfo = fullCalendarId ? fullCalendarId.mailboxInfo : getUserMailboxInfo();
    let email;
    let getCalendarFolderConfigResponse;

    if (
        calendarEntry.CalendarFolderType == CalendarFolderTypeEnum.PublicCalendarFolder &&
        getUserConfiguration().SessionSettings.DefaultPublicFolderMailbox
    ) {
        email = getUserConfiguration().SessionSettings.DefaultPublicFolderMailbox;
    } else {
        email = getEmailAddress(calendarEntry.OwnerEmailAddress, mailboxInfo);
    }

    try {
        getCalendarFolderConfigResponse = await getCalendarFolderConfigurationService(
            folderId({
                Id: calculatedFolderId,
            }),
            email
        );
    } catch (error) {
        // There are cases wherein GetCalendarFolderConfigurationService seems to return 200's with mailboxNotFound messages
        // and in certain scenarios 500s with a redirect. In certain hybrid topologies, when online mailboxes are trying to access
        // a shared calendar from on-prem, a 500 is returned and a 200 with error code 31 on other lookup failures.
        // Either ways, the current behavior is to fall back to making a call to GetUserAvailability and fetch f/b data for this calendar.
        logUsage('getCalendarFolderConfigurationServiceFailed');
        markCalendarEntryAsValid(calendarEntry);
        return;
    }

    if (getCalendarFolderConfigResponse) {
        // We need to mark CalendarEntry as valid even when we don't have a FolderId to support previous
        // calendar sharing model that uses LinkedCalendarEntries. We won't have a FolderId in scenarios
        // where the user doesn't have permissions on the calendar and can only get FreeBusy information
        markCalendarEntryAsValid(calendarEntry);
        if (calendarEntry.CalendarFolderType == CalendarFolderTypeEnum.PublicCalendarFolder) {
            addPublicFolderCalendarsToStore(
                getCalendarFolderConfigResponse,
                calendarEntry,
                mailboxInfo
            );
        } else {
            updateStoreWithActualFolderId(
                getCalendarFolderConfigResponse,
                calendarEntry,
                mailboxInfo,
                calendarId
            );
        }
    } else {
        throw new Error(`Unable to get folder for calendar ${calendarId}`);
    }
}

function addPublicFolderCalendarsToStore(
    getCalendarFolderConfigResponse: GetCalendarFolderConfigurationResponse,
    calendarEntry: LocalCacheForRemoteCalendarEntry,
    mailboxInfo: MailboxInfo
) {
    let calendarFolder = getCalendarFolderConfigResponse.CalendarFolder;
    if (calendarFolder) {
        if (!calendarEntry.CalendarFolderId) {
            calendarEntry.CalendarFolderId =
                getCalendarFolderConfigResponse.CalendarFolder.FolderId;
        }
        const PFmailboxId = calendarFolder.ReplicaList[0];
        addToCalendarsCache(calendarEntry, calendarFolder, {
            type: 0,
            mailboxId: PFmailboxId,
            requestOptions: createExplicitLogonRequest(PFmailboxId, mailboxInfo),
            returnModernConversationItems: mailboxInfo.returnModernConversationItems,
        });
    }
}

function updateStoreWithActualFolderId(
    getCalendarFolderConfigResponse: GetCalendarFolderConfigurationResponse,
    calendarEntry: LocalCacheForRemoteCalendarEntry,
    mailboxInfo: MailboxInfo,
    calendarId: string
) {
    let calendarFolder = getCalendarFolderConfigResponse.CalendarFolder;
    if (calendarFolder && calendarFolder.EffectiveRights && calendarFolder.EffectiveRights.Read) {
        if (getCalendarFolderConfigResponse.MasterList) {
            calendarEntry.RemoteCategories = getCalendarFolderConfigResponse.MasterList;
        }

        if (calendarId == calendarEntry.OwnerEmailAddress) {
            calendarUpdated(calendarId, {
                id: {
                    id: calendarEntry.ItemId.Id,
                    mailboxInfo: mailboxInfo,
                },
                calculatedFolderId: calendarFolder.FolderId.Id,
            });

            removeCalendarWithIDFromCalendarsCache(calendarId);

            // now we add the actual calendarFolder to the cache
            addToCalendarsCache(calendarEntry, calendarFolder, mailboxInfo);
        }
    }
}

function getEmailAddress(ownerEmailAddress: string, mailboxInfo: MailboxInfo) {
    if (ownerEmailAddress) {
        return ownerEmailAddress;
    } else if (mailboxInfo) {
        return mailboxInfo.mailboxId;
    } else {
        return null;
    }
}
